import { Grid } from '@mantine/core';
import { ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { LogoDimensions } from 'src/components/common/MarketplaceLogo';
import { ListingPriceInput } from 'src/components/Input/ListingPriceInput';
import { Content } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  ListingDetailsUpdateInput,
  MarketplaceAccountSetting,
  UiCurrency,
} from 'src/WebApiController';

import { BulkMarketplacePriceFormInput } from './BulkChangeMarketplacePriceAction.types';
import * as styles from './BulkEditMarketplacePriceMobileContent.css';
import {
  formatOfficeFeePercent,
  getOfficeFeeBucketContentId,
  OfficePriceRow,
} from './officePriceRows.utils';

type StubHubManualPriceProps = {
  MarketplaceLog: React.FC<LogoDimensions>;
  disabled: boolean;
  currency: UiCurrency;
  sellerFee: number;
  onPriceChanged: () => void;
  /** Defaults to the account-level StubHub price (allInPrice <-> listPrice) */
  field?: 'allInPrice' | `officePriceUpdates.${number}.allInPrice`;
  otherField?: 'listPrice' | `officePriceUpdates.${number}.listPrice`;
  label?: ReactNode;
};

export const StubHubManualPrice = ({
  MarketplaceLog,
  disabled,
  currency,
  sellerFee,
  onPriceChanged,
  field = 'allInPrice',
  otherField = 'listPrice',
  label = <Content id={ContentId.StubHubPrice} />,
}: StubHubManualPriceProps) => {
  return (
    <Stack direction="column" width="full" gap="l">
      <Grid align="center" style={{ fontSize: vars.typography.fontSize.sm }}>
        <Grid.Col offset={1}>{label}</Grid.Col>
      </Grid>
      <Grid align="center" className={styles.priceTileContainer}>
        <Grid.Col span={1}>
          <MarketplaceLog width={24} height={24} />
        </Grid.Col>
        <Grid.Col span={11}>
          <ListingPriceInput<BulkMarketplacePriceFormInput>
            isBulkEdit
            currencyCode={currency.code}
            rootClassName={styles.priceInput}
            disabled={disabled}
            isAllInPrice
            field={field}
            otherField={otherField}
            fieldToOtherRatio={1 / (1 - sellerFee)}
            onChange={(_) => onPriceChanged()}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

/**
 * Multi-office selection: one StubHub tile per office, bound to
 * officePriceUpdates.{i} and converted with that office's StubHub seller fee.
 */
export const OfficeStubHubManualPrices = ({
  officePriceRows,
  ...rest
}: Omit<
  StubHubManualPriceProps,
  'sellerFee' | 'field' | 'otherField' | 'label'
> & {
  officePriceRows: OfficePriceRow[];
}) => (
  <>
    {officePriceRows.map(
      ({ officeId, officeName, sellerFee, isDomestic }, i) => {
        const bucketContentId = getOfficeFeeBucketContentId(isDomestic);
        return (
          <StubHubManualPrice
            key={`StubHub-office-${officeId}-${isDomestic}`}
            {...rest}
            sellerFee={sellerFee ?? 0}
            field={`officePriceUpdates.${i}.allInPrice`}
            otherField={`officePriceUpdates.${i}.listPrice`}
            label={
              <>
                <Content id={ContentId.StubHubPrice} />
                {' — '}
                {officeName ?? (
                  <>
                    <Content id={ContentId.Office} /> {officeId}
                  </>
                )}
                {bucketContentId ? (
                  <>
                    {' ('}
                    <Content id={bucketContentId} />
                    {')'}
                  </>
                ) : (
                  ` (${formatOfficeFeePercent(sellerFee)}%)`
                )}
              </>
            }
          />
        );
      }
    )}
  </>
);

export const MarketplacePriceInputHeader = () => {
  return (
    <Grid align="center" style={{ fontSize: vars.typography.fontSize.sm }}>
      <Grid.Col offset={1} span={5}>
        <Content id={ContentId.Price} />
      </Grid.Col>
      <Grid.Col span={3.5}>
        <Content id={ContentId.Markup} />
      </Grid.Col>
      <Grid.Col span={2.5}>
        <Content id={ContentId.Override} />
      </Grid.Col>
    </Grid>
  );
};

export const MarketplacePriceInputTile = ({
  MarketplaceLog,
  inputIndex,
  currency,
  marketplaceAccountSettings,
  disabled,
  isPriceOverrideDisabled = false,
  onPriceEdit,
}: {
  /** Multi-office: price override (inputs and switch) is locked, markup stays editable */
  isPriceOverrideDisabled?: boolean;
  inputIndex: number;
  MarketplaceLog: React.FC<LogoDimensions>;
  disabled: boolean;
  currency: UiCurrency;
  marketplaceAccountSettings?: MarketplaceAccountSetting;
  onPriceEdit: (
    newPrice: number,
    pricyByMarketplace: boolean,
    index: number
  ) => void;
}) => {
  const { watch, setValue, register } =
    useFormContext<ListingDetailsUpdateInput>();

  const priceByMarketplaceEnabled = watch(
    `marketplacePriceUpdates.${inputIndex}.priceByMarketplace`
  );
  const listPrice = watch('listPrice');

  const defaultMarkup = useMemo(() => {
    return marketplaceAccountSettings?.markup != null
      ? roundToPrecision(marketplaceAccountSettings.markup * 100, 2)
      : null;
  }, [marketplaceAccountSettings?.markup]);

  return (
    <Grid align="center" className={styles.priceTileContainer}>
      <Grid.Col span={1}>
        <MarketplaceLog width={24} height={24} />
      </Grid.Col>
      <Grid.Col span={5}>
        <ListingPriceInput
          rootClassName={styles.priceInput}
          isBulkEdit
          currencyCode={currency.code}
          disabled={
            disabled || isPriceOverrideDisabled || !priceByMarketplaceEnabled
          }
          isAllInPrice
          field={`marketplacePriceUpdates.${inputIndex}.allInPrice`}
          otherField={`marketplacePriceUpdates.${inputIndex}.listPrice`}
          fieldToOtherRatio={
            1 / (1 - (marketplaceAccountSettings?.sellerFee ?? 0))
          }
          onChange={(newPrice) =>
            onPriceEdit(newPrice, priceByMarketplaceEnabled, inputIndex)
          }
        />
      </Grid.Col>
      <Grid.Col span={3.5}>
        <PosTextField
          rootProps={{
            className: styles.percentageInput,
          }}
          disabled={disabled || Boolean(priceByMarketplaceEnabled)}
          type="number"
          placeholder={`${defaultMarkup}`}
          postfixDisplay="%"
          {...register(`marketplacePriceUpdates.${inputIndex}.markup`)}
        />
      </Grid.Col>
      <Grid.Col span={2.5}>
        <Switch
          disabled={disabled || isPriceOverrideDisabled}
          checked={priceByMarketplaceEnabled}
          onCheckedChange={(isChecked: boolean) => {
            setValue(
              `marketplacePriceUpdates.${inputIndex}.priceByMarketplace`,
              isChecked
            );
            if (!isChecked) {
              // If this is false, set the value to listValue
              const mpListPrice = roundToPrecision(
                (listPrice ?? 0) *
                  (1 + (marketplaceAccountSettings?.markup ?? 0)),
                currency.dec
              );
              setValue(
                `marketplacePriceUpdates.${inputIndex}.listPrice`,
                mpListPrice
              );
            }
            setValue(`marketplacePriceUpdates.${inputIndex}.markup`, null);
          }}
        />
      </Grid.Col>
    </Grid>
  );
};
